import { combineEpics } from "redux-observable";
import { push } from "connected-react-router";
import { of, EMPTY } from "rxjs";
import { ajax } from "rxjs/ajax";
import { ofType } from "redux-observable";
import { mergeMap, catchError, filter, exhaustMap, debounceTime, switchMap } from "rxjs/operators";
import { authHeaders, tokenExists, getOrganisationBaseUrl, onLogout as onFrameLogout } from "frame";
import {
	timesheetActionTypes,
	onAddTimesheetReset,
	onAddTimesheetSaveFailed,
	onAddTimesheetSaveSuccess,
	onTimesheetListReceive,
	onTimesheetListFailed,
	onTimesheetRemoveSuccess,
	onTimesheetRemoveFail,
	onActiveTimesheetRequest,
	onActiveTimesheetReceive,
	refreshTimesheetList,
	onActiveTimesheetFailed,
	onAddTimesheetStartFailed,
	onAddTimesheetStartSuccess,
	onAddTimesheetValueChange,
	onConfirmActiveTimesheetFailed,
	onConfirmActiveTimesheetSuccess,
	onDiscardActiveTimesheetSuccess,
	onDiscardActiveTimesheetFailed,
} from "./timesheet-actions";
import { LIST_TIMESHEETS_ROUTE } from "./timesheet-routes";
import moment from "moment";
import {
	ORGANISATION_FETCH_SUCCESS,
	ORGANISATIONS_CURRENT_ORGANISATION_CHANGED,
} from "features/organisations";
import { GENERIC_ERROR_MESSAGE } from "utils";
import { addQueueItem, removeQueueItem, sendRequested } from "features/queue";
import queueTypes from "features/queue/actions/types";
import uuid from "uuid";

const ACTIVE_TIMESHEET_NOTES_STORAGE_KEY = "sw_active_timesheet_notes_v1";

function readActiveNotesDraft() {
	try {
		const raw = localStorage.getItem(ACTIVE_TIMESHEET_NOTES_STORAGE_KEY);
		return raw ? JSON.parse(raw) : null;
	} catch (e) {
		return null;
	}
}

function writeActiveNotesDraft(organisationId, timesheetId, notes) {
	try {
		localStorage.setItem(
			ACTIVE_TIMESHEET_NOTES_STORAGE_KEY,
			JSON.stringify({
				organisationId,
				timesheetId: timesheetId || "",
				notes: notes ?? "",
			})
		);
	} catch (e) {
		// ignore storage errors
	}
}

function clearPersistedActiveTimesheetNotes() {
	try {
		localStorage.removeItem(ACTIVE_TIMESHEET_NOTES_STORAGE_KEY);
	} catch (e) {
		// ignore
	}
}

const getActiveTimesheetEffect = (action$, state$) =>
	action$.pipe(
		ofType(
			ORGANISATION_FETCH_SUCCESS,
			ORGANISATIONS_CURRENT_ORGANISATION_CHANGED,
			timesheetActionTypes.ACTIVE_TIMESHEET_REQUESTED
		),
		filter(() => tokenExists()),
		filter(() => state$.value.queue.hasNetwork),
		// Drop an in-flight read from the previous user when impersonation or an org switch starts a new one.
		switchMap(() => {
			return ajax
				.get(
					`${getOrganisationBaseUrl(state$.value)}/timesheet/active?_=${Date.now()}`,
					authHeaders()
				)
				.pipe(
					mergeMap((ajaxResponse) => {
						return of(
							onActiveTimesheetReceive(ajaxResponse.response)
						);
					}),
					catchError((error) => {
						return of(onActiveTimesheetFailed(GENERIC_ERROR_MESSAGE));
					})
				);
		})
	);

const addTimesheetEffect = (action$, state$) =>
	action$.pipe(
		ofType(timesheetActionTypes.ADD_TIMESHEET_FORM_SUBMIT),
		// Prevent duplicate creates if the user double-clicks Save (or submit fires twice)
		exhaustMap(() => {
			return ajax
				.post(
					`${getOrganisationBaseUrl(state$.value)}/timesheet/create`,
					{
						siteId: state$.value.timesheetFormReducer.siteId,
						staffId:
							state$.value.timesheetFormReducer.timesheet.staffId,
						startDateTimeUTC: moment(
							state$.value.timesheetFormReducer.timesheet
								.startDate +
								" " +
								state$.value.timesheetFormReducer.timesheet
									.startTime,
							"YYYY-MM-DD HH:mm"
						).toISOString(),
						endDateTimeUTC: moment(
							state$.value.timesheetFormReducer.timesheet
								.endDate +
								" " +
								state$.value.timesheetFormReducer.timesheet
									.endTime,
							"YYYY-MM-DD HH:mm"
						).toISOString(),
						lunchTimeMinutes: parseInt(
							state$.value.timesheetFormReducer.timesheet
								.lunchTime,
							10
						),
						notes: state$.value.timesheetFormReducer.timesheet
							.notes,
						startLatitude:
							state$.value.timesheetFormReducer.timesheet
								.startLatitude,
						startLongitude:
							state$.value.timesheetFormReducer.timesheet
								.startLongitude,
						endLatitude:
							state$.value.timesheetFormReducer.timesheet
								.endLatitude,
						endLongitude:
							state$.value.timesheetFormReducer.timesheet
								.endLongitude,
						organisationTaskTypeId:
							state$.value.timesheetFormReducer.timesheet
								.organisationTaskTypeId,
						equipment:
							state$.value.timesheetFormReducer.equipment || []
					},
					authHeaders()
				)
				.pipe(
					mergeMap((ajaxResponse) => {
						return of(
							onAddTimesheetSaveSuccess(),
							push(LIST_TIMESHEETS_ROUTE)
						);
					}),
					catchError((error) =>
						of(onAddTimesheetSaveFailed(GENERIC_ERROR_MESSAGE))
					)
				);
		})
	);

const updateTimesheetEffect = (action$, state$) =>
	action$.pipe(
		ofType(timesheetActionTypes.UPDATE_TIMESHEET_FORM_SUBMIT),
		mergeMap(() =>
			ajax
				.post(
					`${getOrganisationBaseUrl(state$.value)}/timesheet/update/${
						state$.value.timesheetFormReducer.timesheet.id
					}`,
					{
						siteId: state$.value.timesheetFormReducer.siteId,
						staffId:
							state$.value.timesheetFormReducer.timesheet.staffId,
						startDateTimeUTC: moment(
							state$.value.timesheetFormReducer.timesheet
								.startDate +
								" " +
								state$.value.timesheetFormReducer.timesheet
									.startTime,
							"YYYY-MM-DD HH:mm"
						).toISOString(),
						endDateTimeUTC: moment(
							state$.value.timesheetFormReducer.timesheet
								.endDate +
								" " +
								state$.value.timesheetFormReducer.timesheet
									.endTime,
							"YYYY-MM-DD HH:mm"
						).toISOString(),
						lunchTimeMinutes: parseInt(
							state$.value.timesheetFormReducer.timesheet
								.lunchTime,
							10
						),
						notes: state$.value.timesheetFormReducer.timesheet
							.notes,
						startLatitude:
							state$.value.timesheetFormReducer.timesheet
								.startLatitude,
						startLongitude:
							state$.value.timesheetFormReducer.timesheet
								.startLongitude,
						endLatitude:
							state$.value.timesheetFormReducer.timesheet
								.endLatitude,
						endLongitude:
							state$.value.timesheetFormReducer.timesheet
								.endLongitude,
						organisationTaskTypeId:
							state$.value.timesheetFormReducer.timesheet
								.organisationTaskTypeId,
						equipment:
							state$.value.timesheetFormReducer.equipment || [],
						Id:
							state$.value.timesheetFormReducer.timesheet
								.id
					},
					authHeaders()
				)
				.pipe(
					mergeMap((ajaxResponse) => {
						return of(
							onAddTimesheetSaveSuccess(),
							refreshTimesheetList()
						);
					}),
					catchError((error) =>
						of(onAddTimesheetSaveFailed(GENERIC_ERROR_MESSAGE))
					)
				)
		)
	);

// Coerce form values for API: empty string → null for optional Guids; "" siteId → null (API treats Guid.Empty as null).
const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
const toOptionalGuid = (v) => (v && v !== "" ? v : null);
const toSiteIdGuid = (v) => (v && v !== "" ? v : EMPTY_GUID);
const toNumberOrNull = (v) => (v != null && v !== "" && !Number.isNaN(Number(v)) ? Number(v) : null);

const startActiveTimesheetEffect = (action$, state$) =>
	action$.pipe(
		ofType(timesheetActionTypes.ADD_TIMESHEET_FORM_START_TIMER),
		exhaustMap(() => {
			const startDateTimeUTC =
				state$.value?.timesheetFormReducer?.timesheet?.startDateTimeUTC ||
				moment(
					state$.value.timesheetFormReducer.timesheet.startDate +
						" " +
						state$.value.timesheetFormReducer.timesheet.startTime,
					"YYYY-MM-DD HH:mm"
				).toISOString();

		const body = {
			siteId: toSiteIdGuid(state$.value.timesheetFormReducer.siteId),
			organisationTaskTypeId: toOptionalGuid(state$.value.timesheetFormReducer.organisationTaskTypeId),
			equipment: state$.value.timesheetFormReducer.equipment || [],
			startDateTimeUTC,
				startLatitude: toNumberOrNull(state$.value.timesheetFormReducer.timesheet.startLatitude),
				startLongitude: toNumberOrNull(state$.value.timesheetFormReducer.timesheet.startLongitude),
			};

			// If offline, queue the start request and update UI optimistically
			if (!state$.value.queue.hasNetwork) {
				const itemId = uuid.v4();
				const url = `${getOrganisationBaseUrl(state$.value)}/timesheet/start`;
				const offlineBody = { ...body, syncedFromOffline: true };

				// Store a minimal offline snapshot so the active timer survives page reloads
				try {
					localStorage.setItem(
						"offline_active_timesheet",
					JSON.stringify({
						siteId: state$.value.timesheetFormReducer.siteId,
						organisationTaskTypeId: state$.value.timesheetFormReducer.organisationTaskTypeId,
						equipment: state$.value.timesheetFormReducer.equipment || [],
						startDate: state$.value.timesheetFormReducer.timesheet.startDate,
						startTime: state$.value.timesheetFormReducer.timesheet.startTime,
						startLatitude: state$.value.timesheetFormReducer.timesheet.startLatitude,
						startLongitude: state$.value.timesheetFormReducer.timesheet.startLongitude,
						notes: state$.value.timesheetFormReducer.timesheet.notes || "",
					})
					);
				} catch (e) {
					// ignore storage errors
				}

				return of(
					addQueueItem({ id: itemId, url, body: offlineBody }),
					onAddTimesheetStartSuccess(),
					// Attempt to send in case we come back online quickly
					sendRequested()
				);
			}

			// Online: check token before making request
			const headers = authHeaders();
			if (!tokenExists() || !headers.Authorization) {
				// Token missing or expired, return error
				return of(onFrameLogout());
			}

			// Online: perform request immediately
			return ajax
				.post(`${getOrganisationBaseUrl(state$.value)}/timesheet/start`, body, headers)
				.pipe(
					mergeMap(() => {
						return of(onActiveTimesheetRequest(), onAddTimesheetStartSuccess());
					}),
					catchError((err) => {
						const msg =
							(err?.response?.message) ||
							(err?.response && typeof err.response === "string" ? err.response : null) ||
							GENERIC_ERROR_MESSAGE;
						return of(onAddTimesheetStartFailed(msg));
					})
				);
		})
	);

const confirmActiveTimesheetEffect = (action$, state$) =>
	action$.pipe(
		ofType(timesheetActionTypes.ADD_TIMESHEET_FORM_CONFIRM),
		mergeMap(() => {
			// Compute start in UTC (based solely on the recorded start date/time)
			const startUtc =
				state$.value?.timesheetFormReducer?.timesheet?.startDateTimeUTC ||
				moment(
					state$.value.timesheetFormReducer.timesheet.startDate +
						" " +
						state$.value.timesheetFormReducer.timesheet.startTime,
					"YYYY-MM-DD HH:mm"
				).toISOString();
			const body = {
				siteId: state$.value.timesheetFormReducer.siteId,
				endDateTimeUTC: moment(
					state$.value.timesheetFormReducer.timesheet.endDate +
						" " +
						state$.value.timesheetFormReducer.timesheet.endTime,
					"YYYY-MM-DD HH:mm"
				).toISOString(),
				lunchTimeMinutes: parseInt(
					state$.value.timesheetFormReducer.timesheet.lunchTime,
					10
				),
				notes: state$.value.timesheetFormReducer.timesheet.notes,
				endLatitude: state$.value.timesheetFormReducer.timesheet.endLatitude,
				endLongitude: state$.value.timesheetFormReducer.timesheet.endLongitude,
			organisationTaskTypeId: state$.value.timesheetFormReducer.organisationTaskTypeId,
			equipment: state$.value.timesheetFormReducer.equipment || [],
			// Enrich with start info for offline display
			startDateTimeUTC: startUtc,
				startLatitude: state$.value.timesheetFormReducer.timesheet.startLatitude,
				startLongitude: state$.value.timesheetFormReducer.timesheet.startLongitude,
				staffId: state$.value.timesheetFormReducer.timesheet.staffId,
			};

			// If offline, queue the stop request and update UI optimistically
			if (!state$.value.queue.hasNetwork) {
				const itemId = uuid.v4();
				const url = `${getOrganisationBaseUrl(state$.value)}/timesheet/stop`;
				const form = state$.value.timesheetFormReducer;
				// Match start payload shaping so stop sync does not 400 on empty-string Guids / coords
				const stopBody = {
					siteId: toSiteIdGuid(form.siteId),
					endDateTimeUTC: body.endDateTimeUTC,
					lunchTimeMinutes: body.lunchTimeMinutes,
					notes: body.notes,
					endLatitude: toNumberOrNull(form.timesheet.endLatitude),
					endLongitude: toNumberOrNull(form.timesheet.endLongitude),
				organisationTaskTypeId: toOptionalGuid(form.organisationTaskTypeId),
				equipment: form.equipment || [],
				startDateTimeUTC: startUtc,
				startLatitude: toNumberOrNull(form.timesheet.startLatitude),
				startLongitude: toNumberOrNull(form.timesheet.startLongitude),
				staffId: form.timesheet.staffId,
				syncedFromOffline: true,
				};

				// Clear offline snapshot as we're stopping
				try {
					localStorage.removeItem("offline_active_timesheet");
				} catch (e) {
					// ignore storage errors
				}
				clearPersistedActiveTimesheetNotes();

				// If start is still queued (never reached the server), drop it and sync only stop.
				// Stop can create a completed timesheet when no active row exists, which avoids
				// a brief "Active" server row between start-sync and stop-sync.
				const queuedStart = (state$.value.queue.list || []).find(
					(q) => q && q.url && /\/timesheet\/start$/i.test(q.url)
				);
				const queuedNotes = (state$.value.queue.list || []).filter(
					(q) => q && q.url && /\/timesheet\/active\/notes/i.test(q.url)
				);
				const actions = [];
				if (queuedStart?.id) {
					actions.push(removeQueueItem(queuedStart.id));
				}
				queuedNotes.forEach((q) => {
					if (q?.id) actions.push(removeQueueItem(q.id));
				});
				actions.push(
					addQueueItem({ id: itemId, url, body: stopBody }),
					onConfirmActiveTimesheetSuccess(),
					onAddTimesheetReset(),
					push(LIST_TIMESHEETS_ROUTE),
					sendRequested()
				);
				return of(...actions);
			}

			// Online: check token before making request
			const headers = authHeaders();
			if (!tokenExists() || !headers.Authorization) {
				// Token missing or expired, return error
				return of(onFrameLogout());
			}

			// Online: perform request immediately
			return ajax
				.post(`${getOrganisationBaseUrl(state$.value)}/timesheet/stop`, body, headers)
				.pipe(
					mergeMap(() => {
						// Clear offline snapshot on successful stop
						try {
							localStorage.removeItem("offline_active_timesheet");
						} catch (e) {
							// ignore storage errors
						}
						clearPersistedActiveTimesheetNotes();
						return of(onConfirmActiveTimesheetSuccess(), onAddTimesheetReset(), push(LIST_TIMESHEETS_ROUTE));
					}),
					catchError((error) => {
						// If server says the token is invalid/expired, force logout so user can re-auth
						if (error?.status === 401) {
							return of(onFrameLogout());
						}
						return of(onConfirmActiveTimesheetFailed(GENERIC_ERROR_MESSAGE));
					})
				);
		})
	);

const discardActiveTimesheetEffect = (action$, state$) =>
	action$.pipe(
		ofType(timesheetActionTypes.ADD_TIMESHEET_FORM_DISCARD),
		mergeMap(({ payload }) => {
			// Guard against invalid/undefined payload
			if (!payload) {
				// If no ID provided, just reset the form (handles offline cases)
				// Note: Navigation is handled by the component's onDiscard function
				clearPersistedActiveTimesheetNotes();
				return of(
					onDiscardActiveTimesheetSuccess(),
					onAddTimesheetReset()
				);
			}

			// If offline, we cannot discard a server-side timesheet
			if (!state$.value?.queue?.hasNetwork) {
				return of(
					onDiscardActiveTimesheetFailed(
						"You're offline. Please reconnect to the internet and try discarding again."
					)
				);
			}

			// Ensure we have a valid auth token before making the request
			const headers = authHeaders();
			if (!tokenExists() || !headers.Authorization) {
				return of(onFrameLogout());
			}

			return ajax
				.delete(
					`${getOrganisationBaseUrl(
						state$.value
					)}/timesheet/delete/${payload}`,
					headers
				)
				.pipe(
					mergeMap((ajaxResponse) => {
						clearPersistedActiveTimesheetNotes();
						return of(
							onDiscardActiveTimesheetSuccess(),
							onAddTimesheetReset(),
							push(LIST_TIMESHEETS_ROUTE)
						);
					}),
					catchError((error) => {
						// Prefer a specific message when possible; fall back to generic
						const status = error?.status;

						// If server says the token is invalid/expired, force logout so user can re-auth
						if (status === 401) {
							return of(onFrameLogout());
						}

						const serverMessage =
							error?.response?.message ||
							error?.response?.error ||
							error?.response?.title;

						const message =
							status === 403
									? "You don't have permission to discard this timesheet."
									: status === 404
										? "Timesheet not found. It may have already been deleted."
										: serverMessage || error?.message || GENERIC_ERROR_MESSAGE;

						return of(onDiscardActiveTimesheetFailed(message));
					})
				);
		})
	);

// Restore offline active timesheet snapshot on app rehydrate
const restoreOfflineActiveTimesheetEffect = (action$, state$) =>
	action$.pipe(
		ofType("persist/REHYDRATE"),
		mergeMap(() => {
			// If we already have an active timesheet toggle in state, skip
			const hasActiveToggle = !!state$.value?.timesheetFormReducer?.timesheet?.toggle;
			if (hasActiveToggle) {
				return of();
			}

			let snapshot = null;
			try {
				const raw = localStorage.getItem("offline_active_timesheet");
				if (raw) {
					snapshot = JSON.parse(raw);
				}
			} catch (e) {
				// ignore parse/storage errors
			}

			if (!snapshot) {
				return of();
			}

			// Hydrate fields then mark start success to set toggle + timer message
			return of(
				onAddTimesheetValueChange({
				siteId: snapshot.siteId,
				organisationTaskTypeId: snapshot.organisationTaskTypeId,
				equipment: snapshot.equipment || [],
					timesheet: {
						...state$.value.timesheetFormReducer.timesheet,
						startDate: snapshot.startDate,
						startTime: snapshot.startTime,
						startLatitude: snapshot.startLatitude,
						startLongitude: snapshot.startLongitude,
						notes: snapshot.notes != null ? snapshot.notes : state$.value.timesheetFormReducer.timesheet.notes,
					},
				}),
				onAddTimesheetStartSuccess()
			);
		})
	);

// After server returns an active timesheet, overlay any locally saved notes draft (same org / timesheet).
const mergeStoredActiveTimesheetNotesEpic = (action$, state$) =>
	action$.pipe(
		ofType(timesheetActionTypes.ACTIVE_TIMESHEET_RECEIVED),
		mergeMap(({ payload }) => {
			if (!payload) {
				clearPersistedActiveTimesheetNotes();
				return EMPTY;
			}
			const orgId = state$.value.organisations?.currentOrganisation;
			const serverNotes = payload.notes ?? payload.Notes ?? "";
			const draft = readActiveNotesDraft();
			if (!draft || draft.organisationId !== orgId) {
				return EMPTY;
			}
			const idMatch = !draft.timesheetId || draft.timesheetId === payload.id;
			if (!idMatch) {
				return EMPTY;
			}
			const localNotes = draft.notes ?? "";
			if (localNotes === serverNotes) {
				return EMPTY;
			}
			const st = state$.value.timesheetFormReducer;
			return of(
				onAddTimesheetValueChange({
					...st,
					timesheet: { ...st.timesheet, notes: localNotes },
				})
			);
		})
	);

// Debounced: persist active timesheet notes to localStorage and sync to server (or offline queue).
const persistAndSyncActiveTimesheetNotesEpic = (action$, state$) =>
	action$.pipe(
		ofType(timesheetActionTypes.ADD_TIMESHEET_FORM_VALUE_CHANGE),
		filter(() => state$.value.timesheetFormReducer?.timesheet?.toggle === true),
		debounceTime(800),
		mergeMap(() => {
			const orgId = state$.value.organisations?.currentOrganisation;
			const tr = state$.value.timesheetFormReducer.timesheet;
			const notes = tr.notes ?? "";
			const timesheetId = tr.id;

			if (!orgId) {
				return EMPTY;
			}

			writeActiveNotesDraft(orgId, timesheetId, notes);

			if (!timesheetId) {
				return EMPTY;
			}

			const url = `${getOrganisationBaseUrl(state$.value)}/timesheet/active/notes`;
			const body = { notes };
			const headers = authHeaders();

			if (!state$.value.queue.hasNetwork) {
				if (!tokenExists() || !headers.Authorization) {
					return EMPTY;
				}
				const itemId = uuid.v4();
				return of(addQueueItem({ id: itemId, url, body }), sendRequested());
			}

			if (!tokenExists() || !headers.Authorization) {
				return EMPTY;
			}

			return ajax.post(url, body, headers).pipe(
				mergeMap(() => EMPTY),
				catchError((error) => {
					// No active timesheet on server — clear stale client "Active" flag instead of
					// endlessly queueing notes that will 404 and churn the sync pipeline.
					if (error?.status === 404 || error?.status === 400) {
						return of(onActiveTimesheetReceive(null));
					}
					const itemId = uuid.v4();
					return of(addQueueItem({ id: itemId, url, body }), sendRequested());
				})
			);
		})
	);

// Clear offline snapshot when server confirms active timesheet (source of truth)
const clearSnapshotOnActiveReceiveEffect = (action$, state$) =>
	action$.pipe(
		ofType(timesheetActionTypes.ACTIVE_TIMESHEET_RECEIVED),
		mergeMap(({ payload }) => {
			try {
				localStorage.removeItem("offline_active_timesheet");
			} catch (e) {
				// ignore
			}
			// Also drop any queued notes syncs once we know there is no active timesheet
			if (!payload) {
				const notesItems = (state$.value?.queue?.list || []).filter(
					(q) => q && q.url && /\/timesheet\/active\/notes/i.test(q.url)
				);
				if (notesItems.length) {
					return of(...notesItems.map((q) => removeQueueItem(q.id)));
				}
			}
			return EMPTY;
		})
	);

// After a queued timesheet start/stop lands, refresh the list so Pending → Submitted
// without a manual pull. Exclude notes sync — that fires often and would flicker the list.
// After stop, re-fetch active so menu/top-bar toggle matches the server (clears stale Active).
const refreshTimesheetListAfterQueueSyncEffect = (action$, state$) =>
	action$.pipe(
		ofType(queueTypes.QUEUE_SEND_SUCCEEDED),
		filter(() => state$.value.queue.hasNetwork),
		filter((action) => {
			const url = action?.payload?.url || "";
			return /\/timesheet\/(start|stop)(\/|$|\?)/i.test(url);
		}),
		debounceTime(800),
		mergeMap((action) => {
			const url = action?.payload?.url || "";
			const isStop = /\/timesheet\/stop/i.test(url);
			if (isStop) {
				return of(onActiveTimesheetRequest(), refreshTimesheetList());
			}
			return of(refreshTimesheetList());
		})
	);

export const timesheetEffects = combineEpics(
	getActiveTimesheetEffect,
	updateTimesheetEffect,
	addTimesheetEffect,
	startActiveTimesheetEffect,
	confirmActiveTimesheetEffect,
	discardActiveTimesheetEffect,
	restoreOfflineActiveTimesheetEffect,
	clearSnapshotOnActiveReceiveEffect,
	refreshTimesheetListAfterQueueSyncEffect,
	mergeStoredActiveTimesheetNotesEpic,
	persistAndSyncActiveTimesheetNotesEpic,
);
